import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, Mail, Lock, ArrowRight, Check } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface KlartextAuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  initialEmail?: string;
  initialMode?: 'login' | 'signup';
}

type AuthMode = 'login' | 'signup';

export function KlartextAuthModal({ open, onOpenChange, onSuccess, initialEmail = '', initialMode = 'signup' }: KlartextAuthModalProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Sync email state when initialEmail prop changes
  React.useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    setIsLoading(true);

    try {
      if (mode === 'signup') {
        // Use the published Lovable URL which is always in the allowlist
        const redirectTo = `https://klartech-web-maker.lovable.app/klartext-check`;
        
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectTo,
          },
        });
        
        if (error) {
          if (error.message.includes('already registered')) {
            toast({
              title: 'E-Mail bereits registriert',
              description: 'Bitte melden Sie sich an oder verwenden Sie eine andere E-Mail.',
              variant: 'destructive',
            });
            setMode('login');
          } else {
            throw error;
          }
        } else {
          toast({
            title: 'Konto erstellt!',
            description: 'Sie können jetzt den Klartext-Check nutzen.',
          });
          onSuccess?.();
          onOpenChange(false);
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast({
              title: 'Anmeldung fehlgeschlagen',
              description: 'E-Mail oder Passwort ist falsch.',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
        } else {
          toast({
            title: 'Willkommen zurück!',
          });
          onSuccess?.();
          onOpenChange(false);
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Bitte versuchen Sie es später erneut.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    '5 kostenlose Checks pro Monat',
    'Speichern Sie Ihre Check-Historie',
    'Premium-Pläne mit unbegrenzten Checks',
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {mode === 'signup' ? 'Kostenlos registrieren' : 'Anmelden'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {mode === 'signup' 
              ? 'Erstellen Sie ein Konto für mehr Sicherheitschecks.' 
              : 'Melden Sie sich mit Ihrem Konto an.'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'signup' && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-4">
            <ul className="space-y-2">
              {benefits.map((benefit, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-green-800">
                  <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="ihre@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="password"
                type="password"
                placeholder={mode === 'signup' ? 'Mindestens 6 Zeichen' : '••••••••'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10"
                required
                minLength={6}
                disabled={isLoading}
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 py-3 font-semibold text-white"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {mode === 'signup' ? 'Konto erstellen' : 'Anmelden'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </form>

        <div className="text-center text-sm text-slate-600">
          {mode === 'signup' ? (
            <p>
              Bereits registriert?{' '}
              <button
                onClick={() => setMode('login')}
                className="font-semibold text-primary hover:underline"
              >
                Anmelden
              </button>
            </p>
          ) : (
            <p>
              Noch kein Konto?{' '}
              <button
                onClick={() => setMode('signup')}
                className="font-semibold text-primary hover:underline"
              >
                Registrieren
              </button>
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
